/**
 * Company list provenance — which badges a row shows, and whether it can be
 * refreshed.
 *
 * The backend guarantees ONE `Company` node per real company, so one row can
 * carry two chains (a researched API chain and a PDF upload). That split three
 * questions apart which used to share a single id-prefix answer:
 *
 *   - which exchange to badge       → `resolveRegionBadge`
 *   - does a PDF chain exist        → `hasPdfChain`
 *   - is there anything to refresh  → `canRefresh`
 *
 * A merged company keeps the `ind_pdf_*` id it was created with, so every
 * id-prefix verdict about it is stale. These live in their own module both to
 * be unit-testable and because exporting them from the page component tripped
 * react-refresh.
 */

import { isPdfCompany } from '../components/financial/utils/financeSource';
import type { Company } from './companyTypes';

type CompanyRegion = 'US' | 'UK' | 'INDIA' | 'PDF';

export function deriveCompanyRegion(company: Pick<Company, 'source' | 'companyId'>): CompanyRegion {
  // PDF-ingested companies FIRST: their ids ("ind_pdf_*", "uk_pdf_*") would
  // otherwise fall into the scraped ind_/uk_ prefix buckets below.
  //
  // NOTE this answers "which pipeline/exchange", and 'PDF' here means only
  // "no exchange could be determined". It is NOT the answer to "does this
  // company have a PDF chain" — `hasPdfChain` owns that question, off the
  // backend's report-derived `sources`. The two were previously the same
  // value, which is why a merged company (a `ind_pdf_*` id that has since
  // been researched) lost its exchange badge entirely.
  if (isPdfCompany(company.companyId, company.source)) return 'PDF';

  const source = (company.source || '').toLowerCase();
  if (source === 'sec' || source === 'sec_edgar') return 'US';
  if (source === 'companies_house') return 'UK';
  if (source === 'bse' || source === 'nse' || source === 'moneycontrol') return 'INDIA';

  // Fallback: derive from companyId prefix used by the backend
  // (comp_<cik> for SEC, uk_* for Companies House, ind_* for BSE/NSE).
  const id = (company.companyId || '').toLowerCase();
  if (id.startsWith('comp_')) return 'US';
  if (id.startsWith('uk_')) return 'UK';
  if (id.startsWith('ind_')) return 'INDIA';
  return 'PDF';
}

export const REGION_BADGE: Record<CompanyRegion, string> = {
  US: 'SEC',
  UK: 'Companies House',
  INDIA: 'BSE/NSE',
  PDF: 'PDF',
};

/**
 * The exchange badge to show, or null when there is genuinely no exchange.
 *
 * `deriveCompanyRegion` keys off the company id, which on a merged company
 * still carries its original ingest prefix — so a researched company that
 * began life as a PDF upload derives 'PDF' and would show no exchange at all.
 * When the backend reports an API chain, that verdict is known to be wrong,
 * so `source` (which does name a real pipeline) decides instead.
 *
 * Returns null only when no exchange can be established, in which case the
 * PDF badge alone carries the row's provenance.
 */
export function resolveRegionBadge(company: Company): CompanyRegion | null {
  const region = deriveCompanyRegion(company);
  if (region !== 'PDF') return region;

  // An API chain exists, so 'PDF' came from the id prefix and is stale.
  if (chainSources(company).includes('API')) {
    const fromSource = deriveCompanyRegion({
      companyId: '',
      source: company.source,
    });
    return fromSource === 'PDF' ? null : fromSource;
  }

  return null;
}

/**
 * Can this company's financial data be refreshed?
 *
 * Refresh re-runs a research pipeline, which only exists for a chain that was
 * researched — there is nothing to re-fetch for a PDF upload. Previously this
 * was `deriveCompanyRegion(...) !== 'PDF'`, which keyed off the id prefix and
 * so refused a merged company that DOES have a refreshable API chain, purely
 * because it happened to be created by a PDF upload first.
 *
 * `resolveRegionBadge` already answers exactly this: it returns an exchange
 * when one can be established and null when the company is PDF-only.
 */
export function canRefresh(company: Company): boolean {
  return resolveRegionBadge(company) !== null;
}

/**
 * Does this company hold a PDF extraction chain?
 *
 * A merged company keeps its researched id and its researched `source`, so
 * neither can reveal the PDF chain — only an explicit signal from the backend
 * can. Both shapes it might arrive in are accepted; absent both, this is
 * false and no badge renders. It is deliberately NOT inferred from the id
 * prefix, which reports false for exactly the merged companies that matter.
 */
export function hasPdfChain(company: Company): boolean {
  if (company.hasPdfExtraction) return true;
  return chainSources(company).includes('PDF');
}

/**
 * Which chains this company holds, uppercased — `["API", "PDF"]` or `["PDF"]`.
 *
 * Prefers the backend's `sources`, which is derived from the reports and is
 * the only trustworthy answer. The id-prefix fallback that used to back this
 * up is deliberately GONE: a merged company keeps its original researched id,
 * so the prefix reported false for exactly the companies that hold two chains.
 * An empty array means "not served" — callers fall back to `source`, never to
 * a guess.
 */
export function chainSources(company: Company): string[] {
  return (company.sources ?? [])
    .filter(Boolean)
    .map((source) => source.toUpperCase());
}
